import { useEffect, useRef, useState } from 'react';
import { AdminUser, Task, TaskStatus } from '../../pm/types';
import { displayName } from '../../pm/constants';
import { assignTaskOwner, deleteTask, subscribeTaskComments, updateTask } from '../../pm/firebase';
import { timeAgo } from '../../pm/timeAgo';
import { linkify } from '../../pm/linkify';
import { copyToClipboard, taskShareUrl } from '../../pm/share';
import OwnerPicker from './OwnerPicker';
import StatusPicker from './StatusPicker';
import DueDatePicker from './DueDatePicker';
import CommentThread from './CommentThread';

interface Props {
  task: Task;
  admins: AdminUser[];
  currentUid: string;
  // Drag-and-drop reorder within a workstream; props are optional so
  // existing callers without drag wiring still compile.
  isDragging?: boolean;
  dropIndicator?: 'before' | 'after' | null;
  onDragStartTask?: (id: string) => void;
  onDragOverTask?: (id: string, position: 'before' | 'after') => void;
  onDragLeaveTask?: (id: string) => void;
  onDropTask?: (id: string) => void;
  onDragEndTask?: () => void;
  // True when this task was deep-linked via the URL hash — adds a transient
  // highlight class so the user can see what they were sent to.
  highlighted?: boolean;
}

const TaskRow: React.FC<Props> = ({
  task,
  admins,
  currentUid,
  isDragging = false,
  dropIndicator = null,
  onDragStartTask,
  onDragOverTask,
  onDragLeaveTask,
  onDropTask,
  onDragEndTask,
  highlighted = false,
}) => {
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const [linkCopied, setLinkCopied] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(task.label);
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState(task.description || '');
  const [, forceRerender] = useState(0);
  const titleInputRef = useRef<HTMLInputElement | null>(null);
  const descriptionRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    // Lightweight comment count subscription so we can show "N comments"
    // even when the thread is collapsed.
    const unsub = subscribeTaskComments(task.id, (items) => setCommentCount(items.length));
    return unsub;
  }, [task.id]);

  useEffect(() => {
    if (!editingTitle) setTitleDraft(task.label);
  }, [task.label, editingTitle]);

  useEffect(() => {
    if (!editingDescription) setDescriptionDraft(task.description || '');
  }, [task.description, editingDescription]);

  useEffect(() => {
    if (editingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [editingTitle]);

  useEffect(() => {
    if (editingDescription && descriptionRef.current) {
      descriptionRef.current.focus();
      const end = descriptionRef.current.value.length;
      descriptionRef.current.setSelectionRange(end, end);
    }
  }, [editingDescription]);

  useEffect(() => {
    const id = window.setInterval(() => forceRerender((n) => n + 1), 30000);
    return () => window.clearInterval(id);
  }, []);

  async function handleStatusChange(s: TaskStatus) {
    await updateTask(task.id, { status: s }, currentUid);
  }

  async function handleOwnerChange(uid: string) {
    await assignTaskOwner(
      task.id,
      task.projectId,
      uid,
      task.ownerId || '',
      task.label,
      currentUid,
    );
  }

  async function handleDueDateChange(dueDate: number | null) {
    await updateTask(task.id, { dueDate }, currentUid);
  }

  async function commitTitleEdit() {
    const next = titleDraft.trim();
    if (!next) {
      setTitleDraft(task.label);
      setEditingTitle(false);
      return;
    }
    if (next === task.label) {
      setEditingTitle(false);
      return;
    }
    await updateTask(task.id, { label: next }, currentUid);
    setEditingTitle(false);
  }

  function cancelTitleEdit() {
    setTitleDraft(task.label);
    setEditingTitle(false);
  }

  async function commitDescriptionEdit() {
    const next = descriptionDraft;
    if (next === (task.description || '')) {
      setEditingDescription(false);
      return;
    }
    await updateTask(task.id, { description: next }, currentUid);
    setEditingDescription(false);
  }

  function cancelDescriptionEdit() {
    setDescriptionDraft(task.description || '');
    setEditingDescription(false);
  }

  async function handleDelete() {
    const msg = commentCount > 0
      ? `Delete this task and its ${commentCount} ${commentCount === 1 ? 'comment' : 'comments'}?`
      : 'Delete this task?';
    if (!window.confirm(msg)) return;
    await deleteTask(task.id);
  }

  async function handleCopyLink() {
    const url = taskShareUrl(task.projectId, task.id);
    const ok = await copyToClipboard(url);
    if (ok) {
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 1500);
    } else {
      window.prompt('Copy this task link:', url);
    }
  }

  const updatedByEmail = admins.find((a) => a.uid === task.updatedBy)?.email;
  const updatedByDisplay = displayName(updatedByEmail);

  return (
    <div
      id={`task-${task.id}`}
      className={[
        'pm-task',
        isDragging ? 'pm-task-dragging' : '',
        dropIndicator === 'before' ? 'pm-task-drop-before' : '',
        dropIndicator === 'after' ? 'pm-task-drop-after' : '',
        highlighted ? 'pm-task-highlighted' : '',
      ].filter(Boolean).join(' ')}
      onDragOver={(e) => {
        if (!onDragOverTask) return;
        // Stop propagation so the parent workstream's onDragOver doesn't
        // also fire — it would otherwise mark the workstream as a
        // drop-at-end target while we already know exactly which task is
        // being targeted.
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        const rect = e.currentTarget.getBoundingClientRect();
        const midY = rect.top + rect.height / 2;
        const position: 'before' | 'after' = e.clientY < midY ? 'before' : 'after';
        onDragOverTask(task.id, position);
      }}
      onDragLeave={(e) => {
        if (!onDragLeaveTask) return;
        e.stopPropagation();
        onDragLeaveTask(task.id);
      }}
      onDrop={(e) => {
        if (!onDropTask) return;
        e.preventDefault();
        e.stopPropagation();
        onDropTask(task.id);
      }}
    >
      <div className="pm-task-row">
        {/* Drag handle. Only the grip is draggable so clicks on pills /
            title / description / comments behave normally. */}
        {onDragStartTask ? (
          <span
            className="pm-task-grip"
            draggable={true}
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData('text/plain', task.id);
              // Use the whole task as the drag ghost, not just the grip dots.
              const taskEl = e.currentTarget.closest('.pm-task') as HTMLElement | null;
              if (taskEl) e.dataTransfer.setDragImage(taskEl, 12, 12);
              onDragStartTask(task.id);
            }}
            onDragEnd={() => onDragEndTask && onDragEndTask()}
            onClick={(e) => e.stopPropagation()}
            aria-label="Drag to reorder"
            title="Drag to reorder"
          >
            <svg width="8" height="14" viewBox="0 0 8 14" fill="currentColor" aria-hidden="true">
              <circle cx="1.5" cy="2" r="1" />
              <circle cx="6.5" cy="2" r="1" />
              <circle cx="1.5" cy="7" r="1" />
              <circle cx="6.5" cy="7" r="1" />
              <circle cx="1.5" cy="12" r="1" />
              <circle cx="6.5" cy="12" r="1" />
            </svg>
          </span>
        ) : null}
        <StatusPicker status={task.status} onChange={handleStatusChange} />
        <OwnerPicker ownerId={task.ownerId || ''} admins={admins} onChange={handleOwnerChange} />
        <div className="pm-task-content">
          {editingTitle ? (
            <input
              ref={titleInputRef}
              className="pm-task-edit-input pm-task-edit-title"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={commitTitleEdit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitTitleEdit();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelTitleEdit();
                }
              }}
            />
          ) : (
            <div
              className={`pm-task-title ${task.status === 'done' ? 'done' : ''}`}
              onClick={() => setEditingTitle(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') setEditingTitle(true); }}
              title="Click to edit"
            >
              {linkify(task.label, { stopPropagation: true })}
            </div>
          )}

          {editingDescription ? (
            <textarea
              ref={descriptionRef}
              className="pm-task-edit-input pm-task-edit-description"
              value={descriptionDraft}
              onChange={(e) => setDescriptionDraft(e.target.value)}
              onBlur={commitDescriptionEdit}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  commitDescriptionEdit();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelDescriptionEdit();
                }
              }}
              rows={2}
              placeholder="Add a description…"
            />
          ) : task.description ? (
            <div
              className="pm-task-description"
              onClick={() => setEditingDescription(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') setEditingDescription(true); }}
              title="Click to edit"
            >
              {linkify(task.description, { stopPropagation: true })}
            </div>
          ) : (
            <button
              type="button"
              className="pm-link-button pm-task-add-description"
              onClick={() => setEditingDescription(true)}
            >
              + Add description
            </button>
          )}
        </div>
      </div>
      <div className="pm-task-meta">
        <div className="pm-task-meta-left">
          <button className="pm-link-button" onClick={() => setCommentsOpen((v) => !v)}>
            {commentsOpen
              ? `Hide comments${commentCount > 0 ? ` (${commentCount})` : ''}`
              : commentCount > 0
                ? `${commentCount} ${commentCount === 1 ? 'comment' : 'comments'}`
                : 'Add comment'}
          </button>
        </div>
        <div className="pm-task-meta-right">
          <DueDatePicker
            dueDate={task.dueDate}
            taskDone={task.status === 'done'}
            onChange={handleDueDateChange}
          />
          <span className="pm-task-updated">
            {task.updatedAt ? `Updated by ${updatedByDisplay}, ${timeAgo(task.updatedAt)}` : 'Not yet updated'}
          </span>
          <span className="pm-task-meta-sep" aria-hidden="true">•</span>
          <button
            className="pm-link-button"
            onClick={handleCopyLink}
            title="Copy a shareable link that opens this task"
          >
            {linkCopied ? 'Link copied' : 'Copy link'}
          </button>
          <span className="pm-task-meta-sep" aria-hidden="true">•</span>
          <button className="pm-link-button pm-danger" onClick={handleDelete}>Delete</button>
        </div>
      </div>
      {commentsOpen ? (
        <div className="pm-task-comments">
          <CommentThread
            taskId={task.id}
            projectId={task.projectId}
            admins={admins}
            currentUid={currentUid}
          />
        </div>
      ) : null}
    </div>
  );
};

export default TaskRow;
